import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const services = [
  {
    icon: 'ri-plane-line',
    image: 'https://public.readdy.ai/ai/img_res/56566f6d8a18224a5dc3858c787431f5.jpg',
    title: 'Airport Transportation',
    description: 'Real-time flight tracking, professional luggage assistance, and on-time transfers to CHS International, Charleston Executive, and Mt. Pleasant Regional Airports.',
    href: '/services/airport-transportation',
    hasPage: true,
  },
  {
    icon: 'ri-heart-2-line',
    image: 'https://readdy.ai/api/search-image?query=elegant%20white%20limousine%20wedding%20flowers%20venue%20entrance%20romantic%20luxury_bridal_transportation_premium&width=400&height=280&seq=svc-wedding-v2&orientation=landscape',
    title: 'Wedding Transportation',
    description: 'Coordinated, stress-free transportation for brides, grooms, wedding parties, and guests. Your perfect day deserves perfect arrivals.',
    href: '/services/wedding-transportation',
    hasPage: true,
  },
  {
    icon: 'ri-briefcase-4-line',
    image: 'https://readdy.ai/api/search-image?query=executive_businessman_black_luxury_sedan_corporate_professional_driver_office_building_premium&width=400&height=280&seq=svc-corporate-v2&orientation=landscape',
    title: 'Corporate Transportation',
    description: 'Discreet, punctual executive travel for meetings, airport transfers, and corporate events. Arrive ready, composed, and on time — every time.',
    href: '/services/corporate-transportation',
    hasPage: true,
  },
  {
    icon: 'ri-calendar-event-line',
    image: 'https://readdy.ai/api/search-image?query=luxury%20limousine%20downtown%20Charleston%20SC%20night%20event%20concert%20group%20transportation%20premium%20dark&width=400&height=280&seq=svc-event-v2&orientation=landscape',
    title: 'Event Transportation',
    description: 'Concerts, galas, private parties, and nights out in Downtown Charleston. We handle group logistics so you can enjoy the evening without a care.',
    href: '#contact',
    hasPage: false,
  },
  {
    icon: 'ri-car-line',
    image: 'https://readdy.ai/api/search-image?query=private%20chauffeur%20luxury%20black%20car%20service%20scenic%20route%20premium%20personal_transportation&width=400&height=280&seq=svc-private-v2&orientation=landscape',
    title: 'Private Chauffeur',
    description: 'Hourly or full-day private chauffeur service tailored entirely to your schedule. Explore Charleston at your own pace in total comfort.',
    href: '#contact',
    hasPage: false,
  },
  {
    icon: 'ri-graduation-cap-line',
    image: 'https://readdy.ai/api/search-image?query=prom_night_luxury_limousine_students_dressed_formal_elegant_safe_transportation_premium&width=400&height=280&seq=svc-prom-v2&orientation=landscape',
    title: 'Prom Transportation',
    description: 'Parents trust us for safety. Students love the luxury. Professional chauffeurs ensure your prom night is memorable, responsible, and truly unforgettable.',
    href: '#contact',
    hasPage: false,
  },
  {
    icon: 'ri-map-pin-2-line',
    image: 'https://readdy.ai/api/search-image?query=Charleston_SC_historic_district_rainbow_row_sight_seeing_tour_luxury_car_cobblestone_streets&width=400&height=280&seq=svc-sights-v2&orientation=landscape',
    title: 'Charleston Sights Tours',
    description: 'Discover Charleston\'s history, architecture, and hidden gems with a knowledgeable local chauffeur. A private, flexible tour on your terms.',
    href: '#contact',
    hasPage: false,
  },
  {
    icon: 'ri-ship-2-line',
    image: 'https://readdy.ai/api/search-image?query=cruise_ship_port_Charleston_SC_luxury_car_transfer_professional_transportation_waterfront_premium&width=400&height=280&seq=svc-cruise-v2&orientation=landscape',
    title: 'Cruises & Port Transport',
    description: 'On-time, stress-free transportation to and from Charleston\'s cruise port and waterfront. Start and end your voyage the right way — in luxury.',
    href: '#contact',
    hasPage: false,
  },
];

export default function HomeSectionThree() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section id="services" className="relative py-28 overflow-hidden" style={{ backgroundColor: '#0d0d0d' }}>
      <div className="absolute inset-0 opacity-3" style={{ backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(201,169,74,0.08) 0%, transparent 60%)' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div ref={ref} className="section-reveal text-center mb-16">
          <span className="text-gold text-xs tracking-widest uppercase font-semibold">What We Offer</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3 leading-tight">
            Transportation Services<br />
            <span className="text-gold">Designed Around You</span>
          </h2>
          <div className="h-0.5 w-16 bg-gold mx-auto mt-6 mb-4"></div>
          <p className="text-white/45 max-w-xl mx-auto text-sm leading-relaxed">
            Premium luxury transportation tailored to every occasion — professional, reliable, and always on time.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/10">
          {services.map((svc) => (
            <div key={svc.title} className="group bg-[#0d0d0d] hover:bg-[#111] transition-colors duration-300 luxury-card flex flex-col">
              <div className="w-full h-44 overflow-hidden">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 group-hover:bg-gold/20 transition-colors mb-4 flex-shrink-0">
                  <i className={`${svc.icon} text-gold text-lg`}></i>
                </div>
                <h3 className="text-white font-semibold text-sm mb-2 group-hover:text-gold transition-colors leading-snug">{svc.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed flex-grow">{svc.description}</p>
                {svc.hasPage ? (
                  <Link
                    to={svc.href}
                    className="mt-5 text-gold text-xs tracking-widest uppercase font-semibold hover:text-gold-light transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    Learn More
                    <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-line text-xs"></i></div>
                  </Link>
                ) : (
                  <a
                    href={svc.href}
                    className="mt-5 text-gold text-xs tracking-widest uppercase font-semibold hover:text-gold-light transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    Book Now
                    <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-line text-xs"></i></div>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="#contact"
            className="inline-flex items-center gap-3 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-all px-12 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer font-bold"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-check-line text-sm"></i></div>
            Reserve Your Ride — Book Now
          </a>
        </div>
      </div>
    </section>
  );
}
